import React, { useState, useEffect, useCallback } from "react";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb";
import FadeAlert, { FadeAlertType } from "../../components/FadeAlert/FadeAlert";
import { AnnexData } from "../../types/annex.ts";
import PolicyService from "../../services/PolicyService/PolicyService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import AnnexSumsAndDatesForm from "../../components/AnnexSumsAndDates/AnnexSumsAndDates.tsx";
import SearchPolicy from "../../components/SearchPolicy/SearchPolicy.tsx";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import usePolicyForm from "../../hooks/usePolicyForm.tsx";
import {
  normalizeAnnexValues,
  normalizeDate,
  revertNormalizeDate,
} from "../../utils/NormalizationUtil.ts";
import moment from "moment";
import { useLocation } from "react-router-dom";
import { PolicyFullObject } from "../../types/insurance.ts";

// ─── Constants ────────────────────────────────────────────────────────────────

const makeBlankAnnex = (policyNumber = "0"): AnnexData => ({
  annexId: 0,
  policyNumber,
  insuredSum: "",
  policySum: "",
  contractDate: moment().format("DD/MM/YYYY"),
  startDate: moment().add(1, "days").format("DD/MM/YYYY"),
  reportDate: moment().format("DD/MM/YYYY"),
  endDate: moment().add(1, "year").subtract(1, "days").format("DD/MM/YYYY"),
  policyAdditionalData: "",
  divisionId: "",
  paymentAccount: "",
});

// ─── Component ────────────────────────────────────────────────────────────────

const PolicyAnnex: React.FC = () => {
  const { jwt } = useAuth();
  const location = useLocation();
  const { policyNumber: routePolicyNumber } = location.state || {};

  const [annexes, setAnnexes] = useState<number[]>([0]);
  const [currentAnnexIndex, setCurrentAnnexIndex] = useState(0);
  const [paymentAccounts, setPaymentAccounts] = useState<{
    [key: string]: string;
  }>({});
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const {
    policyData,
    setPolicyData,
    insuranceCompanies,
    policyTypes,
    offices,
    handleSearchPolicy,
  } = usePolicyForm();

  const [currentAnnex, setCurrentAnnex] = useState<AnnexData>(() =>
    makeBlankAnnex(routePolicyNumber)
  );

  // ── Initial search from router state ────────────────────────────────────
  useEffect(() => {
    if (routePolicyNumber) {
      handleSearchPolicy(routePolicyNumber);
    }
  }, [routePolicyNumber]);

  // ── Fetch payment accounts when insurance company changes ────────────────
  useEffect(() => {
    if (!policyData.insuranceCompanyId) return;

    const fetchPaymentAccounts = async () => {
      try {
        const fetched: string[] =
          await PolicyService.fetchPaymentAccountsByCompanyId(
            policyData.insuranceCompanyId,
            jwt
          );
        setPaymentAccounts(
          fetched.reduce<{ [key: string]: string }>((acc, account) => {
            acc[account] = account;
            return acc;
          }, {})
        );
      } catch (err: any) {
        setError(err.message);
      }
    };

    fetchPaymentAccounts();
  }, [policyData.insuranceCompanyId, jwt]);

  // ── Fetch annex list when policy changes ─────────────────────────────────
  const fetchAnnexes = useCallback(
    async (policyNum: string) => {
      try {
        const fetchedPolicyData = await PolicyService.fetchPolicyByPolicyNumber(
          policyNum,
          jwt
        );

        if (fetchedPolicyData?.annexes?.length > 0) {
          const newAnnexes = [0, ...fetchedPolicyData.annexes];
          setAnnexes(newAnnexes);
          setCurrentAnnexIndex(newAnnexes.length - 1);
        } else {
          setAnnexes([0]);
          setCurrentAnnexIndex(0);
        }
      } catch (err: any) {
        setError(err.message);
      }
    },
    [jwt]
  );

  useEffect(() => {
    if (policyData.policyNumber) {
      fetchAnnexes(policyData.policyNumber);
    }
  }, [policyData.policyNumber, fetchAnnexes]);

  // ── Fetch individual annex data when selection changes ───────────────────
  useEffect(() => {
    const annexId = annexes[currentAnnexIndex];

    if (!annexId) {
      setCurrentAnnex(
        makeBlankAnnex(policyData.policyNumber || routePolicyNumber)
      );
      return;
    }

    const fetchAnnex = async () => {
      try {
        const annexData = await PolicyService.fetchAnnexDataById(annexId, jwt);
        setCurrentAnnex({
          ...annexData,
          startDate: normalizeDate(annexData.startDate),
          endDate: normalizeDate(annexData.endDate),
          contractDate: normalizeDate(annexData.contractDate),
          reportDate: normalizeDate(annexData.reportDate),
        });
      } catch (err: any) {
        setError(err.message);
      }
    };

    fetchAnnex();
  }, [
    annexes,
    currentAnnexIndex,
    jwt,
    policyData.policyNumber,
    routePolicyNumber,
  ]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleAnnexChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;

      if (name === "policyNumber") {
        setPolicyData((prev) => ({ ...prev, policyNumber: value }));
      }

      setCurrentAnnex((prev) => ({ ...prev, [name]: value }));
    },
    [setPolicyData]
  );

  const saveAnnexData = async () => {
    try {
      const normalizedAnnex = normalizeAnnexValues(
        {
          ...currentAnnex,
          policyNumber: policyData.policyId,
          divisionId: policyData.divisionId,
          startDate: revertNormalizeDate(currentAnnex.startDate),
          endDate: revertNormalizeDate(currentAnnex.endDate),
          reportDate: revertNormalizeDate(currentAnnex.reportDate),
          contractDate: revertNormalizeDate(currentAnnex.contractDate),
          policySum: currentAnnex.policySum === "" ? 0 : currentAnnex.policySum,
          insuredSum:
            currentAnnex.insuredSum === "" ? 0 : currentAnnex.insuredSum,
        },
        true
      );

      await PolicyService.saveAnnexData(normalizedAnnex, jwt);
      setSuccess("Успешен запис на добавък");

      const policyNum = policyData.policyNumber || routePolicyNumber;
      await fetchAnnexes(policyNum);
      await handleSearchPolicy(policyNum);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAnnexSelection = useCallback(
    (selectedValue: number) => {
      const selectedIndex = annexes.indexOf(Number(selectedValue));
      setCurrentAnnexIndex(selectedIndex);
    },
    [annexes]
  );

  const annexOptions = annexes.reduce<{ [key: string]: string }>(
    (acc, annexId, index) => {
      acc[annexId] = annexId === 0 ? "Нов" : String(index);
      return acc;
    },
    {}
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Breadcrumb pageName="Добавъци" />

      {error && (
        <FadeAlert
          alert={error}
          type={FadeAlertType.error}
          onClose={() => setError(null)}
        />
      )}
      {success && (
        <FadeAlert
          alert={success}
          type={FadeAlertType.success}
          onClose={() => setSuccess(null)}
        />
      )}

      <SearchPolicy
        policyData={policyData}
        onSearch={handleSearchPolicy}
        handleChange={handleAnnexChange}
        resetForm={() => setPolicyData({} as PolicyFullObject)}
      />

      <div className="mb-4">
        <div className="flex items-center justify-between">
          {annexes?.length > 0 && (
            <>
              <h2 className="mt-2 text-xl font-bold text-black">
                {currentAnnexIndex === 0
                  ? `Нов добавък`
                  : `Добавък ${currentAnnexIndex} от ${annexes.length - 1}`}
              </h2>
              <div>
                <SelectGroup
                  placeholder="Изберете Добавък"
                  options={annexOptions}
                  classNameAdditional="bg-white min-w-[100px] max-w-[100px]"
                  value={annexes[currentAnnexIndex]}
                  onChange={handleAnnexSelection}
                />
              </div>
            </>
          )}
        </div>

        <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark mt-4 flex gap-4 rounded-sm border bg-white px-6 py-6">
          <div className="w-1/3">
            <label className="mb-1 ml-1 block text-xs font-medium text-black dark:text-white">
              Застрахователна компания
            </label>
            <SelectGroup
              placeholder="Изберете ЗК"
              classNameAdditional="bg-white"
              options={insuranceCompanies}
              value={policyData.insuranceCompanyId}
              onChange={(selectedValue: string) =>
                handleAnnexChange({
                  target: { name: "insuranceCompanyId", value: selectedValue },
                } as React.ChangeEvent<HTMLInputElement>)
              }
            />
          </div>
          <div className="w-1/3">
            <label className="mb-1 ml-1 block text-xs font-medium text-black dark:text-white">
              Вид полица
            </label>
            <SelectGroup
              placeholder="ГО"
              options={policyTypes}
              classNameAdditional="bg-white"
              value={policyData.policyTypeId}
              onChange={(selectedValue: string) =>
                handleAnnexChange({
                  target: { name: "policyTypeId", value: selectedValue },
                } as React.ChangeEvent<HTMLInputElement>)
              }
            />
          </div>
          <div className="w-1/3">
            <label className="mb-1 ml-1 block text-xs font-medium text-black dark:text-white">
              Офис
            </label>
            <SelectGroup
              placeholder="Изберете Офис"
              options={offices}
              classNameAdditional="bg-white"
              value={policyData.divisionId}
              onChange={(selectedValue: string) =>
                handleAnnexChange({
                  target: { name: "divisionId", value: selectedValue },
                } as React.ChangeEvent<HTMLInputElement>)
              }
            />
          </div>
        </div>

        <AnnexSumsAndDatesForm
          annexData={currentAnnex}
          handleChange={handleAnnexChange}
          saveAnnexData={saveAnnexData}
          paymentAccounts={paymentAccounts}
          policyEndDate={policyData.endDate}
        />
      </div>
    </>
  );
};

export default PolicyAnnex;
